import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Calendar, Check, Eye } from "lucide-react";

interface Submission {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  serviceNeeded: string | null;
  prospectiveStartDate: string | null;
  prospectiveEndDate: string | null;
  crossStreets: string | null;
  frequency: string | null;
  message: string | null;
  read: boolean;
  createdAt: string;
}

const serviceLabels: Record<string, string> = {
  vip: "VIP Recurring Dog Walks",
  occasional: "Occasional Dog Walking",
  petsitting: "Pet Sitting Visits",
  pettaxi: "Pet Taxi",
};

const frequencyLabels: Record<string, string> = {
  "one-time": "One-time",
  "occasional": "Occasional / as-needed",
  "weekly": "Weekly",
  "2-3-week": "2–3 times a week",
  "weekdays": "Weekdays (Mon–Fri)",
  "daily": "Daily",
  "other": "Other",
};

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}

export default function ContactManager() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Submission | null>(null);

  async function loadSubmissions() {
    try {
      const data = await apiFetch("/contact/submissions");
      setSubmissions(data);
    } catch (err) {
      console.error("Failed to load submissions", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSubmissions();
  }, []);

  async function markAsRead(id: number) {
    await apiFetch(`/contact/submissions/${id}/read`, { method: "PATCH" });
    setSubmissions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, read: true } : s))
    );
  }

  function handleSelect(sub: Submission) {
    setSelected(sub);
    if (!sub.read) markAsRead(sub.id);
  }

  const unreadCount = submissions.filter((s) => !s.read).length;

  if (loading) {
    return <p className="text-muted-foreground py-8 text-center">Loading submissions...</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif font-bold">Contact Submissions</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {submissions.length} total{unreadCount > 0 && `, ${unreadCount} unread`}
          </p>
        </div>
      </div>

      {submissions.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow-sm text-center">
          <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No contact submissions yet.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
            {submissions.map((sub) => (
              <button
                key={sub.id}
                onClick={() => handleSelect(sub)}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  selected?.id === sub.id
                    ? "bg-primary/10 border-primary"
                    : sub.read
                    ? "bg-white border-gray-200 hover:bg-gray-50"
                    : "bg-blue-50 border-blue-200 hover:bg-blue-100"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-medium ${!sub.read ? "font-bold" : ""}`}>
                    {sub.firstName} {sub.lastName}
                  </span>
                  {!sub.read && (
                    <span className="w-2.5 h-2.5 bg-primary rounded-full flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate">{sub.email}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(sub.createdAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            {selected ? (
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-serif font-bold">
                    {selected.firstName} {selected.lastName}
                  </h3>
                  {selected.read && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Read
                    </span>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${selected.email}`} className="text-primary hover:underline">
                      {selected.email}
                    </a>
                  </div>

                  {selected.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${selected.phone}`} className="hover:underline">
                        {selected.phone}
                      </a>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">
                      {new Date(selected.createdAt).toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>

                  {selected.serviceNeeded && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Service Needed</p>
                      <p>{serviceLabels[selected.serviceNeeded] || selected.serviceNeeded}</p>
                    </div>
                  )}

                  {(selected.prospectiveStartDate || selected.prospectiveEndDate || selected.crossStreets || selected.frequency) && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-3">Service Details</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {selected.prospectiveStartDate && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Start Date</p>
                            <p>{formatDate(selected.prospectiveStartDate)}</p>
                          </div>
                        )}
                        {selected.prospectiveEndDate && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">End Date</p>
                            <p>{formatDate(selected.prospectiveEndDate)}</p>
                          </div>
                        )}
                        {selected.crossStreets && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Cross Streets</p>
                            <p>{selected.crossStreets}</p>
                          </div>
                        )}
                        {selected.frequency && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">Frequency</p>
                            <p>{frequencyLabels[selected.frequency] || selected.frequency}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selected.message && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Message</p>
                      <p className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{selected.message}</p>
                    </div>
                  )}

                  <div className="pt-4">
                    <a href={`mailto:${selected.email}`}>
                      <Button className="flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Reply via Email
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white p-12 rounded-lg shadow-sm border text-center">
                <Eye className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Select a submission to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
